/**
 * Bundle helper functions for reward tier management
 * Uses App Bridge graphqlCall for Shopify Admin API
 * Based on: https://shopify.dev/docs/apps/build/product-merchandising/bundles/add-product-fixed-bundle
 */

import { graphqlCall } from './Common';

/**
 * Get product options for a product (needed for bundle creation)
 * @param {string} productId - Product GID
 * @returns {Promise<Array>} Product options array
 */
export async function getProductOptions(productId) {
    if (!productId) return [];

    try {
        const query = {
            query: `
                query GetProductOptions($id: ID!) {
                    product(id: $id) {
                        id
                        options {
                            id
                            name
                            values
                        }
                    }
                }
            `,
            variables: {
                id: productId
            }
        };

        const response = await graphqlCall(query);
        return response.data?.product?.options || [];
    } catch (error) {
        console.warn('Could not load product options:', error);
        return [];
    }
}

/**
 * Create a product fixed bundle
 * Based on: https://shopify.dev/docs/apps/build/product-merchandising/bundles/add-product-fixed-bundle#step-1-create-a-bundle
 * @param {string} title - Bundle title
 * @param {Array} components - Array of component objects with productId, quantity, and optionSelections
 * @returns {Promise<Object>} Bundle operation object
 */
export async function createProductBundle(title, components) {
    if (!title || !components || components.length === 0) {
        throw new Error('Title and components are required');
    }

    const query = {
        query: `
            mutation ProductBundleCreate($input: ProductBundleCreateInput!) {
                productBundleCreate(input: $input) {
                    productBundleOperation {
                        id
                        status
                    }
                    userErrors {
                        message
                        field
                    }
                }
            }
        `,
        variables: {
            input: {
                title: title,
                components: components
            }
        }
    };

    const response = await graphqlCall(query);

    if (response.errors) {
        throw new Error(response.errors[0]?.message || 'Failed to create bundle');
    }

    if (response.data?.productBundleCreate?.userErrors?.length > 0) {
        const errorMessages = response.data.productBundleCreate.userErrors.map(e => e.message).join(', ');
        throw new Error(errorMessages || 'Failed to create bundle');
    }

    return response.data?.productBundleCreate?.productBundleOperation || null;
}

/**
 * Poll the status of a bundle operation
 * Based on: https://shopify.dev/docs/apps/build/product-merchandising/bundles/add-product-fixed-bundle#step-2-poll-the-status-of-the-bundle-creation-job
 * @param {string} operationId - Bundle operation GID
 * @param {number} maxAttempts - Maximum number of polling attempts (default: 30)
 * @param {number} delayMs - Delay between polling attempts in milliseconds (default: 1000)
 * @returns {Promise<Object>} Operation status with product ID when complete
 */
export async function pollBundleOperation(operationId, maxAttempts = 30, delayMs = 1000) {
    if (!operationId) {
        throw new Error('Operation ID is required');
    }

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
        try {
            const query = {
                query: `
                    query ProductBundleOperation($id: ID!) {
                        productOperation(id: $id) {
                            ... on ProductBundleOperation {
                                id
                                status
                                product {
                                    id
                                }
                                userErrors {
                                    field
                                    message
                                    code
                                }
                            }
                        }
                    }
                `,
                variables: {
                    id: operationId
                }
            };

            const response = await graphqlCall(query);
            const operation = response.data?.productOperation;

            if (operation?.userErrors?.length > 0) {
                const errorMessages = operation.userErrors.map(e => e.message).join(', ');
                throw new Error(errorMessages || 'Bundle operation failed');
            }

            if (operation?.status === 'COMPLETE') {
                return {
                    status: 'COMPLETE',
                    productId: operation.product?.id,
                    operationId: operation.id
                };
            }

            if (operation?.status === 'FAILED') {
                throw new Error('Bundle operation failed');
            }

            // If still processing, wait before next attempt
            if (attempt < maxAttempts - 1) {
                await new Promise(resolve => setTimeout(resolve, delayMs));
            }
        } catch (error) {
            if (attempt === maxAttempts - 1) {
                throw error;
            }
            await new Promise(resolve => setTimeout(resolve, delayMs));
        }
    }

    throw new Error('Bundle operation timed out');
}

/**
 * Update a product bundle
 * Based on: https://shopify.dev/docs/apps/build/product-merchandising/bundles/add-product-fixed-bundle#step-3-update-the-bundle
 * @param {string|number} bundleProductId - Bundle product ID (will be normalized to GID)
 * @param {string} title - Bundle title
 * @param {Array} components - Array of component objects with productId, quantity, and optionSelections
 * @returns {Promise<Object>} Bundle operation object
 */
export async function updateProductBundle(bundleProductId, title, components) {
    if (!bundleProductId || !title || !components || components.length === 0) {
        throw new Error('Bundle product ID, title, and components are required');
    }

    // Normalize bundle product ID to GID format
    const normalizedId = normalizeProductGID(bundleProductId);
    if (!normalizedId) {
        throw new Error(`Invalid bundle product ID format: ${bundleProductId}`);
    }

    const query = {
        query: `
            mutation ProductBundleUpdate($input: ProductBundleUpdateInput!) {
                productBundleUpdate(input: $input) {
                    productBundleOperation {
                        id
                        status
                    }
                    userErrors {
                        message
                        field
                    }
                }
            }
        `,
        variables: {
            input: {
                productId: normalizedId,
                title: title,
                components: components
            }
        }
    };

    const response = await graphqlCall(query);

    if (response.errors) {
        throw new Error(response.errors[0]?.message || 'Failed to update bundle');
    }

    if (response.data?.productBundleUpdate?.userErrors?.length > 0) {
        const errorMessages = response.data.productBundleUpdate.userErrors.map(e => e.message).join(', ');
        throw new Error(errorMessages || 'Failed to update bundle');
    }

    return response.data?.productBundleUpdate?.productBundleOperation || null;
}

/**
 * Normalize product ID to GID format
 * @param {string|number} productId - Product ID (can be numeric, GID format, or partial GID)
 * @returns {string|null} Normalized GID or null if invalid
 */
function normalizeProductGID(productId) {
    if (!productId) return null;

    // If already in GID format, return as is
    if (typeof productId === 'string' && productId.includes('gid://')) {
        return productId;
    }

    // Extract numeric ID
    let numericId = null;
    if (typeof productId === 'number') {
        numericId = productId;
    } else if (typeof productId === 'string') {
        // Try to extract from GID format or parse as number
        const match = productId.match(/\/(\d+)$/);
        if (match) {
            numericId = parseInt(match[1], 10);
        } else if (/^\d+$/.test(productId)) {
            numericId = parseInt(productId, 10);
        }
    }

    if (!numericId || isNaN(numericId)) {
        return null;
    }

    return `gid://shopify/Product/${numericId}`;
}

/**
 * Get bundle product details
 * @param {string} productId - Product GID (will be normalized)
 * @returns {Promise<Object|null>} Bundle product details or null if not found/not a bundle
 */
export async function getBundleProduct(productId) {
    if (!productId) return null;

    try {
        const normalizedId = normalizeProductGID(productId);
        if (!normalizedId) {
            console.warn(`Invalid product ID format: ${productId}`);
            return null;
        }

        const query = {
            query: `
                query GetBundleProduct($id: ID!) {
                    product(id: $id) {
                        id
                        __typename
                        title
                        handle
                        status
                    }
                }
            `,
            variables: {
                id: normalizedId
            }
        };

        const response = await graphqlCall(query);

        if (response.errors) {
            console.warn('Error querying bundle product:', response.errors[0]?.message);
            return null;
        }

        const product = response.data?.product;

        if (!product) {
            // Product doesn't exist
            return null;
        }

        if (product.__typename !== 'ProductBundle') {
            // Product exists but is not a bundle
            return null;
        }

        return product;
    } catch (error) {
        console.warn('Could not get bundle product:', error.message || error);
        return null;
    }
}

// Cache for bundle product checks during save operations
let bundleCheckCache = new Map();

/**
 * Clear the bundle check cache (call at start of save operation)
 */
export function clearBundleCheckCache() {
    bundleCheckCache.clear();
}

/**
 * Batch check multiple products for bundle status in a single GraphQL query
 * @param {Array<string|number>} productIds - Array of product IDs
 * @returns {Promise<Map<string, boolean>>} Map of productId (GID) -> isBundle boolean
 */
async function batchIsBundleProduct(productIds) {
    if (!productIds || productIds.length === 0) {
        return new Map();
    }

    const results = new Map();
    const normalizedIds = [];
    const idMap = new Map(); // Map normalized GID to original productId

    // Normalize all product IDs and check cache
    for (const productId of productIds) {
        if (!productId) continue;

        let bundleId = null;
        if (typeof productId === 'string' && productId.includes('gid://')) {
            bundleId = productId;
        } else {
            bundleId = normalizeProductGID(productId);
        }

        if (!bundleId) {
            console.warn(`Invalid product ID format: ${productId}`);
            results.set(String(productId), false);
            continue;
        }

        // Check cache first
        if (bundleCheckCache.has(bundleId)) {
            results.set(bundleId, bundleCheckCache.get(bundleId));
            continue;
        }

        normalizedIds.push(bundleId);
        idMap.set(bundleId, productId);
    }

    if (normalizedIds.length === 0) {
        return results;
    }

    try {
        // Build GraphQL query with aliases for each product
        const queryParts = normalizedIds.map((id, index) => {
            const alias = `product${index}`;
            return `
                ${alias}: product(id: $id${index}) {
                    id
                    bundleComponents(first: 50) {
                        nodes {
                            quantity
                        }
                    }
                }
            `;
        });

        const variables = {};
        normalizedIds.forEach((id, index) => {
            variables[`id${index}`] = id;
        });

        const query = {
            query: `
                query BatchIsBundle(${normalizedIds.map((_, i) => `$id${i}: ID!`).join(', ')}) {
                    ${queryParts.join('\n')}
                }
            `,
            variables
        };

        const response = await graphqlCall(query);

        if (response.errors) {
            console.warn('Error in batch bundle check:', response.errors[0]?.message);
            // Fallback: mark all as false
            normalizedIds.forEach(id => {
                results.set(id, false);
                bundleCheckCache.set(id, false);
            });
            return results;
        }

        // Process results
        normalizedIds.forEach((id, index) => {
            const alias = `product${index}`;
            const product = response.data?.[alias];
            
            if (!product) {
                results.set(id, false);
                bundleCheckCache.set(id, false);
                return;
            }

            const bundleComponents = product.bundleComponents?.nodes || [];
            const isBundle = bundleComponents.length > 0;
            
            results.set(id, isBundle);
            bundleCheckCache.set(id, isBundle);
        });

        return results;
    } catch (error) {
        console.warn('Error in batch bundle check:', error.message || error);
        // Fallback: mark all as false
        normalizedIds.forEach(id => {
            results.set(id, false);
            bundleCheckCache.set(id, false);
        });
        return results;
    }
}

/**
 * Check if a product is a bundle product using bundleComponents query
 * Uses cache if available, otherwise makes GraphQL call
 * @param {string|number} productId - Product ID (can be numeric, GID format, or partial GID)
 * @returns {Promise<boolean>} True if product is a bundle, false otherwise
 */
async function isBundleProduct(productId) {
    if (!productId) return false;

    try {
        // If productId is already in GID format, use it directly
        // Otherwise, normalize it to GID format
        let bundleId = null;
        if (typeof productId === 'string' && productId.includes('gid://')) {
            // Already a valid GID format, use as-is
            bundleId = productId;
        } else {
            // Normalize to GID format
            bundleId = normalizeProductGID(productId);
        }

        if (!bundleId) {
            console.warn(`Invalid product ID format: ${productId}`);
            return false;
        }

        // Check cache first
        if (bundleCheckCache.has(bundleId)) {
            return bundleCheckCache.get(bundleId);
        }

        const query = {
            query: `
                query IsBundle($id: ID!) {
                    product(id: $id) {
                        id
                        title
                        bundleComponents(first: 50) {
                            nodes {
                                quantity
                                componentProduct {
                                    id
                                    title
                                }
                            }
                        }
                    }
                }
            `,
            variables: {
                id: bundleId
            }
        };

        const response = await graphqlCall(query);

        if (response.errors) {
            console.warn('Error querying bundle product:', response.errors[0]?.message);
            const result = false;
            bundleCheckCache.set(bundleId, result);
            return result;
        }

        const product = response.data?.product;
        if (!product) {
            const result = false;
            bundleCheckCache.set(bundleId, result);
            return result;
        }

        // If bundleComponents.nodes.length > 0 → Product is a bundle
        // If bundleComponents is empty → Not a bundle
        const bundleComponents = product.bundleComponents?.nodes || [];
        const result = bundleComponents.length > 0;
        bundleCheckCache.set(bundleId, result);
        return result;
    } catch (error) {
        console.warn('Could not check if product is bundle:', error.message || error);
        return false;
    }
}

/**
 * GraphQL call with specific API version 2025-10
 * Used for operations that require API version 2025-10+ (e.g., UNLISTED product status)
 * @param {Object} query - GraphQL query object with query and variables
 * @returns {Promise<Object>} GraphQL response
 */
async function graphqlCallWithVersion2025_10(query) {
    // Check if App Bridge is available (window.shopify exists when app is embedded)
    if (window.shopify) {
        try {
            // Use API version 2025-10 in the endpoint URL
            let grphData = await fetch('shopify:admin/api/2025-10/graphql.json', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(query)
            });
            grphData = await grphData.json();
            return grphData;
        } catch (error) {
            console.error('GraphQL call error (API 2025-10):', error);
            return ({
                data: {},
                errors: [{ message: error.message || 'GraphQL request failed' }]
            });
        }
    }
    return ({ data: false, errors: [{ message: 'App Bridge is not available' }] });
}

/**
 * Set product status to UNLISTED using API version 2025-10
 * According to Shopify docs: https://shopify.dev/docs/api/admin-graphql/latest/enums/productstatus
 * UNLISTED: The product is active but you need a direct link to view it. 
 * The product doesn't show up in search, collections, or product recommendations.
 * Available from API version 2025-10 and up.
 * @param {string} productId - Product GID
 * @returns {Promise<boolean>} True if successful
 */
async function setProductStatusUnlisted(productId) {
    if (!productId) return false;

    try {
        const query = {
            query: `
                mutation SetProductStatusUnlisted($product: ProductUpdateInput!) {
                    productUpdate(product: $product) {
                        product {
                            id
                            status
                        }
                        userErrors {
                            field
                            message
                        }
                    }
                }
            `,
            variables: {
                product: {
                    id: productId,
                    status: 'UNLISTED'
                }
            }
        };

        // Use API version 2025-10 for this operation
        const response = await graphqlCallWithVersion2025_10(query);

        if (response.errors) {
            console.warn('Error setting product status to UNLISTED:', response.errors[0]?.message);
            return false;
        }

        if (response.data?.productUpdate?.userErrors?.length > 0) {
            const errorMsg = response.data.productUpdate.userErrors[0].message;
            console.warn('User errors setting product status to UNLISTED:', errorMsg);
            return false;
        }

        if (response.data?.productUpdate?.product?.id) {
            return true;
        }

        return false;
    } catch (error) {
        console.warn('Could not set product status to UNLISTED:', error);
        return false;
    }
}

/**
 * Update bundle product description dynamically
 * Updates descriptionHtml field with reward description
 * Note: ProductInput only supports descriptionHtml, not description field
 * @param {string} productId - Product GID
 * @param {string} description - Description text (can be HTML or plain text)
 * @returns {Promise<boolean>} True if successful
 */
async function updateBundleProductDescription(productId, description) {
    if (!productId || !description) return false;

    try {
        // Normalize productId to GID format
        let productGid = productId;
        if (typeof productId === 'number') {
            productGid = `gid://shopify/Product/${productId}`;
        } else if (!productId.includes('gid://')) {
            productGid = `gid://shopify/Product/${productId}`;
        }

        // Check if description contains HTML tags
        const isHtml = /<[a-z][\s\S]*>/i.test(description);
        
        // Prepare input object - only descriptionHtml is supported in ProductInput (not description field)
        const inputData = {
            id: productGid
        };

        if (isHtml) {
            // Description contains HTML, set descriptionHtml directly
            inputData.descriptionHtml = description;
        } else {
            // Plain text description - convert to HTML format
            // Replace newlines with paragraph breaks and wrap in paragraph tags
            const htmlDescription = description
                .split('\n')
                .filter(line => line.trim().length > 0)
                .map(line => `<p>${line.trim()}</p>`)
                .join('');
            
            // If no content after processing, wrap entire description in paragraph
            inputData.descriptionHtml = htmlDescription || `<p>${description}</p>`;
        }

        const query = {
            query: `
                mutation ProductUpdate($input: ProductInput!) {
                    productUpdate(input: $input) {
                        product {
                            id
                            descriptionHtml
                        }
                        userErrors {
                            field
                            message
                        }
                    }
                }
            `,
            variables: {
                input: inputData
            }
        };

        const response = await graphqlCall(query);

        if (response.errors) {
            console.warn('Error updating bundle product description:', response.errors[0]?.message);
            return false;
        }

        if (response.data?.productUpdate?.userErrors?.length > 0) {
            const errorMessages = response.data.productUpdate.userErrors.map(e => e.message).join(', ');
            console.warn('User errors updating bundle product description:', errorMessages);
            return false;
        }

        if (response.data?.productUpdate?.product?.id) {
            const updatedProduct = response.data.productUpdate.product;
            return true;
        }

        return false;
    } catch (error) {
        console.warn('Could not update bundle product description:', error);
        return false;
    }
}

/**
 * Batch get multiple products with full details including options and variants in a single GraphQL query
 * @param {Array<string>} productIds - Array of product GIDs
 * @returns {Promise<Map<string, Object>>} Map of productId -> product details (or null if not found)
 */
async function batchGetProductWithDetails(productIds) {
    if (!productIds || productIds.length === 0) {
        return new Map();
    }

    const results = new Map();
    const normalizedIds = [];

    // Normalize all product IDs
    for (const productId of productIds) {
        if (!productId) {
            results.set(String(productId), null);
            continue;
        }

        let productGid = productId;
        if (typeof productId === 'number') {
            productGid = `gid://shopify/Product/${productId}`;
        } else if (!productId.includes('gid://')) {
            productGid = normalizeProductGID(productId);
        }

        if (!productGid) {
            results.set(String(productId), null);
            continue;
        }

        normalizedIds.push(productGid);
    }

    if (normalizedIds.length === 0) {
        return results;
    }

    try {
        // Build GraphQL query with aliases for each product
        const queryParts = normalizedIds.map((id, index) => {
            const alias = `product${index}`;
            return `
                ${alias}: product(id: $id${index}) {
                    id
                    title
                    options {
                        id
                        name
                        values
                    }
                    variants(first: 250) {
                        edges {
                            node {
                                id
                                title
                                selectedOptions {
                                    name
                                    value
                                }
                            }
                        }
                    }
                }
            `;
        });

        const variables = {};
        normalizedIds.forEach((id, index) => {
            variables[`id${index}`] = id;
        });

        const query = {
            query: `
                query BatchGetProductWithDetails(${normalizedIds.map((_, i) => `$id${i}: ID!`).join(', ')}) {
                    ${queryParts.join('\n')}
                }
            `,
            variables
        };

        const response = await graphqlCall(query);

        if (response.errors) {
            console.warn('Error in batch product details query:', response.errors[0]?.message);
            // Fallback: mark all as null
            normalizedIds.forEach(id => {
                results.set(id, null);
            });
            return results;
        }

        // Process results
        normalizedIds.forEach((id, index) => {
            const alias = `product${index}`;
            const product = response.data?.[alias] || null;
            results.set(id, product);
        });

        return results;
    } catch (error) {
        console.warn('Error in batch product details query:', error.message || error);
        // Fallback: mark all as null
        normalizedIds.forEach(id => {
            results.set(id, null);
        });
        return results;
    }
}

/**
 * Get product with full details including options and variants
 * @param {string} productId - Product GID
 * @returns {Promise<Object>} Product with options and variants
 */
async function getProductWithDetails(productId) {
    try {
        const query = {
            query: `
                query GetProductWithDetails($id: ID!) {
                    product(id: $id) {
                        id
                        title
                        options {
                            id
                            name
                            values
                        }
                        variants(first: 250) {
                            edges {
                                node {
                                    id
                                    title
                                    selectedOptions {
                                        name
                                        value
                                    }
                                }
                            }
                        }
                    }
                }
            `,
            variables: {
                id: productId
            }
        };

        const response = await graphqlCall(query);
        return response.data?.product || null;
    } catch (error) {
        console.warn('Could not load product details:', error);
        return null;
    }
}

/**
 * Create bundle components from reward products (only selected variants)
 * Based on: https://shopify.dev/docs/apps/build/product-merchandising/bundles/add-product-fixed-bundle#step-1-create-a-bundle
 * 
 * Each selected variant becomes a separate component with its specific option selections.
 * 
 * Component format:
 * {
 *   quantity: 1,
 *   productId: "gid://shopify/Product/1",
 *   optionSelections: [
 *     {
 *       componentOptionId: "gid://shopify/ProductOption/1",
 *       name: "Option Name",
 *       values: ["Value1"]  // Specific value for this variant only
 *     }
 *   ]
 * }
 * 
 * @param {Array} products - Array of reward products with selectedVariants
 * @returns {Promise<Array>} Array of bundle component objects matching Shopify API format
 */
export async function createBundleComponents(products) {
    const components = [];

    // OPTIMIZATION: Collect all product IDs and batch fetch product details in a single GraphQL query
    const productIdsToFetch = [];
    const productGidMap = new Map(); // Map normalized GID to original product object

    // First pass: collect all product IDs and normalize them
    for (const product of products) {
        if (!product.shopify_product_id) continue;

        // OPTIMIZATION: Skip bundle check here - already validated in createOrUpdateRewardBundle
        // Check cache if available, but don't make new GraphQL call
        let productGid = product.shopify_product_id;
        if (typeof product.shopify_product_id === 'string' && product.shopify_product_id.includes('gid://')) {
            productGid = product.shopify_product_id;
        } else {
            productGid = normalizeProductGID(product.shopify_product_id);
        }
        
        // Only check cache, don't make new GraphQL call
        if (bundleCheckCache.has(productGid) && bundleCheckCache.get(productGid)) {
            console.warn(`Skipping bundle product ${product.shopify_product_id} - bundles cannot be used as components`);
            continue;
        }

        if (productGid) {
            productIdsToFetch.push(productGid);
            productGidMap.set(productGid, product);
        }
    }

    // OPTIMIZATION: Batch fetch all product details in a single GraphQL query
    const productDetailsMap = productIdsToFetch.length > 0 
        ? await batchGetProductWithDetails(productIdsToFetch)
        : new Map();

    // Second pass: process products using batch-fetched details
    for (const product of products) {
        if (!product.shopify_product_id) continue;

        try {
            // Get normalized GID for lookup
            let productGid = product.shopify_product_id;
            if (typeof product.shopify_product_id === 'string' && product.shopify_product_id.includes('gid://')) {
                productGid = product.shopify_product_id;
            } else {
                productGid = normalizeProductGID(product.shopify_product_id);
            }
            
            // Skip if bundle (already checked in first pass)
            if (bundleCheckCache.has(productGid) && bundleCheckCache.get(productGid)) {
                continue;
            }

            // Get product details from batch-fetched map
            const productDetails = productDetailsMap.get(productGid) || null;

            if (!productDetails) {
                console.warn(`Could not load details for product ${product.shopify_product_id}`);
                // Fallback: create component without option selections
                components.push({
                    quantity: 1,
                    productId: product.shopify_product_id,
                    optionSelections: []
                });
                continue;
            }

            // Get selected variant IDs (only include these variants in bundle)
            const selectedVariantIds = product.selectedVariants || [];

            // If no selected variants but has shopify_variant_id (backward compatibility)
            if (selectedVariantIds.length === 0 && product.shopify_variant_id) {
                selectedVariantIds.push(product.shopify_variant_id);
            }

            // If no selected variants at all, create one component with all option values (backward compatibility)
            if (selectedVariantIds.length === 0) {
                const optionSelections = [];
                if (productDetails.options && productDetails.options.length > 0) {
                    productDetails.options.forEach((option) => {
                        if (option.values && option.values.length > 0) {
                            optionSelections.push({
                                componentOptionId: option.id,
                                name: option.name,
                                values: option.values
                            });
                        }
                    });
                }
                components.push({
                    quantity: 1,
                    productId: product.shopify_product_id,
                    optionSelections: optionSelections
                });
                continue;
            }

            // Create one component per selected variant
            // Each component will have option selections specific to that variant only
            if (productDetails.variants?.edges && productDetails.options) {
                // Find matching variants from product details
                const variantMap = new Map();
                productDetails.variants.edges.forEach(({ node: variant }) => {
                    variantMap.set(variant.id, variant);
                });

                // For each selected variant, create a separate component
                for (const selectedVariantId of selectedVariantIds) {
                    // Find the variant in product details
                    let matchedVariant = null;

                    // Try to find variant by exact match or partial match
                    for (const [variantId, variant] of variantMap.entries()) {
                        const selectedIdStr = String(selectedVariantId);
                        if (variantId === selectedIdStr ||
                            variantId.endsWith(`/${selectedIdStr}`) ||
                            selectedIdStr.endsWith(`/${variantId.split('/').pop()}`)) {
                            matchedVariant = variant;
                            break;
                        }
                    }

                    if (!matchedVariant) {
                        console.warn(`Could not find variant ${selectedVariantId} in product ${product.shopify_product_id}`);
                        continue;
                    }

                    // Build option selections specific to this variant only
                    const optionSelections = [];

                    if (matchedVariant.selectedOptions && productDetails.options.length > 0) {
                        // For each product option, find the value used by this specific variant
                        productDetails.options.forEach((option) => {
                            // Find the selected option value for this variant
                            const selectedOption = matchedVariant.selectedOptions.find(
                                so => so.name === option.name
                            );

                            if (selectedOption && selectedOption.value) {
                                optionSelections.push({
                                    componentOptionId: option.id,
                                    name: option.name,
                                    values: [selectedOption.value] // Single value for this variant
                                });
                            }
                        });
                    }

                    // Create a component for this specific variant
                    components.push({
                        quantity: 1,
                        productId: product.shopify_product_id,
                        optionSelections: optionSelections
                    });
                }
            } else {
                // Fallback: if no variants found, create component without option selections
                components.push({
                    quantity: 1,
                    productId: product.shopify_product_id,
                    optionSelections: []
                });
            }
        } catch (error) {
            const errorMessage = error.message || '';
            if (errorMessage.includes('componentized products') ||
                errorMessage.includes('invalid as components') ||
                errorMessage.includes('bundle')) {
                console.warn(`Skipping bundle product ${product.shopify_product_id} - bundles cannot be used as components`);
                continue;
            }
            throw error;
        }
    }

    return components;
}

/**
 * Delete a bundle product
 * Note: Bundles are deleted using the standard productDelete mutation
 * @param {string|number} bundleProductId - Bundle product ID (will be normalized to GID)
 * @returns {Promise<boolean>} True if successful
 */
export async function deleteProductBundle(bundleProductId) {
    if (!bundleProductId) {
        throw new Error('Bundle product ID is required');
    }

    try {
        const normalizedId = normalizeProductGID(bundleProductId);
        if (!normalizedId) {
            throw new Error(`Invalid bundle product ID format: ${bundleProductId}`);
        }

        // Verify it's actually a bundle before deleting
        const bundleProduct = await getBundleProduct(normalizedId);
        if (!bundleProduct) {
            throw new Error(`Bundle product ${normalizedId} not found or is not a bundle`);
        }

        const query = {
            query: `
                mutation ProductDelete($input: ProductDeleteInput!) {
                    productDelete(input: $input) {
                        deletedProductId
                        userErrors {
                            field
                            message
                        }
                    }
                }
            `,
            variables: {
                input: {
                    id: normalizedId
                }
            }
        };

        const response = await graphqlCall(query);

        if (response.errors) {
            throw new Error(response.errors[0]?.message || 'Failed to delete bundle');
        }

        if (response.data?.productDelete?.userErrors?.length > 0) {
            const errorMessages = response.data.productDelete.userErrors.map(e => e.message).join(', ');
            throw new Error(errorMessages || 'Failed to delete bundle');
        }

        return !!response.data?.productDelete?.deletedProductId;
    } catch (error) {
        console.error('Error deleting bundle:', error.message || error);
        throw error;
    }
}

/**
 * Get bundle product handle
 * @param {string|number} bundleProductId - Bundle product ID (will be normalized to GID)
 * @returns {Promise<string|null>} Bundle product handle or null
 */
export async function getBundleProductHandle(bundleProductId) {
    if (!bundleProductId) return null;

    try {
        const normalizedId = normalizeProductGID(bundleProductId);
        if (!normalizedId) {
            console.warn(`Invalid bundle product ID format: ${bundleProductId}`);
            return null;
        }

        const query = {
            query: `
                query GetBundleProductHandle($id: ID!) {
                    product(id: $id) {
                        id
                        handle
                    }
                }
            `,
            variables: {
                id: normalizedId
            }
        };

        const response = await graphqlCall(query);

        if (response.errors) {
            console.warn('Error getting bundle product handle:', response.errors[0]?.message);
            return null;
        }

        return response.data?.product?.handle || null;
    } catch (error) {
        console.warn('Could not get bundle product handle:', error.message || error);
        return null;
    }
}

/**
 * Get bundle product variants with their IDs and current prices
 * @param {string} bundleProductId - Bundle product ID (GID format)
 * @returns {Promise<Array>} Array of variant objects with id, price, and compareAtPrice
 */
async function getBundleProductVariants(bundleProductId) {
    if (!bundleProductId) return [];

    try {
        const query = {
            query: `
                query GetBundleVariants($id: ID!) {
                    product(id: $id) {
                        id
                        variants(first: 250) {
                            edges {
                                node {
                                    id
                                    price
                                    compareAtPrice
                                }
                            }
                        }
                    }
                }
            `,
            variables: {
                id: bundleProductId
            }
        };

        const response = await graphqlCall(query);

        if (response.errors) {
            console.warn('Error getting bundle variants:', response.errors[0]?.message);
            return [];
        }

        const variants = response.data?.product?.variants?.edges || [];
        return variants.map(edge => edge.node);
    } catch (error) {
        console.warn('Could not get bundle product variants:', error.message || error);
        return [];
    }
}

/**
 * Calculate total price of all selected products in a reward
 * @param {Array} products - Array of reward products with variants
 * @returns {number} Total price of all selected variants
 */
function calculateTotalProductPrice(products) {
    if (!products || products.length === 0) {
        return 0;
    }

    let total = 0;
    
    for (const product of products) {
        if (!product.variants || !Array.isArray(product.variants)) {
            continue;
        }

        // Get selected variant IDs
        const selectedVariantIds = product.selectedVariants || [];
        
        // If no selected variants, use all variants
        const variantsToSum = selectedVariantIds.length > 0
            ? product.variants.filter(v => selectedVariantIds.includes(v.id))
            : product.variants;

        // Sum up prices of selected variants
        for (const variant of variantsToSum) {
            const price = parseFloat(variant.price) || 0;
            total += price;
        }
    }

    return total;
}

/**
 * Update variant prices for a bundle product
 * @param {string} bundleProductId - Bundle product ID (GID format)
 * @param {string} bundlePrice - Bundle price to set as price (reward_price)
 * @param {number} totalProductPrice - Total price of all selected products to set as compareAtPrice
 * @returns {Promise<boolean>} True if successful
 */
async function updateBundleVariantPrices(bundleProductId, bundlePrice, totalProductPrice) {
    if (!bundleProductId || !bundlePrice) {
        console.warn('Bundle product ID and bundle price are required');
        return false;
    }

    try {
        // Get all variants for the bundle product
        const variants = await getBundleProductVariants(bundleProductId);
        
        if (variants.length === 0) {
            console.warn(`No variants found for bundle product ${bundleProductId}`);
            return false;
        }

        // Prepare variants array for bulk update
        // Set price to bundle price (reward_price), compareAtPrice to total of all selected products
        const variantsInput = variants.map(variant => {
            const variantInput = {
                id: variant.id,
                price: String(bundlePrice) // Set price to bundle price (reward_price)
            };
            
            // Set compareAtPrice to total of all selected products (only if total > 0)
            if (totalProductPrice > 0) {
                variantInput.compareAtPrice = String(totalProductPrice);
            }
            
            return variantInput;
        });

        const mutation = {
            query: `
                mutation UpdateVariantPrices($productId: ID!, $variants: [ProductVariantsBulkInput!]!) {
                    productVariantsBulkUpdate(productId: $productId, variants: $variants) {
                        product {
                            id
                            title
                        }
                        productVariants {
                            id
                            price
                            compareAtPrice
                        }
                        userErrors {
                            field
                            message
                        }
                    }
                }
            `,
            variables: {
                productId: bundleProductId,
                variants: variantsInput
            }
        };

        const response = await graphqlCall(mutation);

        if (response.errors) {
            console.warn('Error updating variant prices:', response.errors[0]?.message);
            return false;
        }

        if (response.data?.productVariantsBulkUpdate?.userErrors?.length > 0) {
            const errorMessages = response.data.productVariantsBulkUpdate.userErrors.map(e => e.message).join(', ');
            console.warn('User errors updating variant prices:', errorMessages);
            return false;
        }

        return true;
    } catch (error) {
        console.warn('Could not update bundle variant prices:', error.message || error);
        return false;
    }
}

/**
 * Helper function to compare two arrays of products for changes
 * @param {Array} oldProducts - Previous products array
 * @param {Array} newProducts - Current products array
 * @returns {boolean} True if products changed
 */
function productsChanged(oldProducts, newProducts) {
    if (!oldProducts && !newProducts) return false;
    if (!oldProducts || !newProducts) return true;
    if (oldProducts.length !== newProducts.length) return true;
    
    // Compare product IDs and selected variants
    const normalizeProduct = (p) => ({
        id: p.shopify_product_id,
        variants: (p.selectedVariants || []).sort().join(',')
    });
    
    const oldNormalized = oldProducts.map(normalizeProduct).sort((a, b) => 
        (a.id || '').localeCompare(b.id || '')
    );
    const newNormalized = newProducts.map(normalizeProduct).sort((a, b) => 
        (a.id || '').localeCompare(b.id || '')
    );
    
    return JSON.stringify(oldNormalized) !== JSON.stringify(newNormalized);
}

/**
 * Update only bundle product title
 * @param {string} bundleProductId - Bundle product GID
 * @param {string} title - Title text
 * @returns {Promise<boolean>} True if successful
 */
export async function updateBundleTitleOnly(bundleProductId, title) {
    if (!bundleProductId || !title) return false;

    try {
        // Normalize productId to GID format
        let productGid = bundleProductId;
        if (typeof bundleProductId === 'number') {
            productGid = `gid://shopify/Product/${bundleProductId}`;
        } else if (!bundleProductId.includes('gid://')) {
            productGid = `gid://shopify/Product/${bundleProductId}`;
        }

        const query = {
            query: `
                mutation ProductUpdate($input: ProductInput!) {
                    productUpdate(input: $input) {
                        product {
                            id
                            title
                        }
                        userErrors {
                            field
                            message
                        }
                    }
                }
            `,
            variables: {
                input: {
                    id: productGid,
                    title: title
                }
            }
        };

        const response = await graphqlCall(query);

        if (response.errors) {
            console.warn('Error updating bundle product title:', response.errors[0]?.message);
            return false;
        }

        if (response.data?.productUpdate?.userErrors?.length > 0) {
            const errorMessages = response.data.productUpdate.userErrors.map(e => e.message).join(', ');
            console.warn('User errors updating bundle product title:', errorMessages);
            return false;
        }

        if (response.data?.productUpdate?.product?.id) {
            return true;
        }

        return false;
    } catch (error) {
        console.warn('Error updating bundle title:', error);
        return false;
    }
}

/**
 * Update bundle product title and description in a single GraphQL call
 * @param {string} bundleProductId - Bundle product GID
 * @param {string} title - Title text
 * @param {string} description - Description text
 * @returns {Promise<boolean>} True if successful
 */
export async function updateBundleTitleAndDescription(bundleProductId, title, description) {
    if (!bundleProductId || (!title && !description)) return false;

    try {
        // Normalize productId to GID format
        let productGid = bundleProductId;
        if (typeof bundleProductId === 'number') {
            productGid = `gid://shopify/Product/${bundleProductId}`;
        } else if (!bundleProductId.includes('gid://')) {
            productGid = `gid://shopify/Product/${bundleProductId}`;
        }

        // Prepare input object
        const inputData = {
            id: productGid
        };

        // Add title if provided
        if (title) {
            inputData.title = title;
        }

        // Add description if provided
        if (description) {
            // Check if description contains HTML tags
            const isHtml = /<[a-z][\s\S]*>/i.test(description);
            
            if (isHtml) {
                // Description contains HTML, set descriptionHtml directly
                inputData.descriptionHtml = description;
            } else {
                // Plain text description - convert to HTML format
                // Replace newlines with paragraph breaks and wrap in paragraph tags
                const htmlDescription = description
                    .split('\n')
                    .filter(line => line.trim().length > 0)
                    .map(line => `<p>${line.trim()}</p>`)
                    .join('');
                
                // If no content after processing, wrap entire description in paragraph
                inputData.descriptionHtml = htmlDescription || `<p>${description}</p>`;
            }
        }

        const query = {
            query: `
                mutation ProductUpdate($input: ProductInput!) {
                    productUpdate(input: $input) {
                        product {
                            id
                            title
                            descriptionHtml
                        }
                        userErrors {
                            field
                            message
                        }
                    }
                }
            `,
            variables: {
                input: inputData
            }
        };

        const response = await graphqlCall(query);

        if (response.errors) {
            console.warn('Error updating bundle product title and description:', response.errors[0]?.message);
            return false;
        }

        if (response.data?.productUpdate?.userErrors?.length > 0) {
            const errorMessages = response.data.productUpdate.userErrors.map(e => e.message).join(', ');
            console.warn('User errors updating bundle product title and description:', errorMessages);
            return false;
        }

        if (response.data?.productUpdate?.product?.id) {
            return true;
        }

        return false;
    } catch (error) {
        console.warn('Error updating bundle title and description:', error);
        return false;
    }
}

/**
 * Update only bundle product description
 * @param {string} bundleProductId - Bundle product GID
 * @param {string} description - Description text
 * @returns {Promise<boolean>} True if successful
 */
export async function updateBundleDescriptionOnly(bundleProductId, description) {
    if (!bundleProductId || !description) return false;
    try {
        return await updateBundleProductDescription(bundleProductId, description);
    } catch (error) {
        console.warn('Error updating bundle description:', error);
        return false;
    }
}

/**
 * Update only bundle product prices
 * @param {string} bundleProductId - Bundle product GID
 * @param {string} rewardPrice - Reward price
 * @param {Array} products - Products array for calculating total price
 * @returns {Promise<boolean>} True if successful
 */
export async function updateBundlePriceOnly(bundleProductId, rewardPrice, products) {
    if (!bundleProductId || !rewardPrice) return false;
    try {
        const totalProductPrice = calculateTotalProductPrice(products);
        return await updateBundleVariantPrices(bundleProductId, rewardPrice, totalProductPrice);
    } catch (error) {
        console.warn('Error updating bundle price:', error);
        return false;
    }
}

/**
 * Create or update a bundle for a reward tier
 * Based on Shopify Bundle API: https://shopify.dev/docs/apps/build/product-merchandising/bundles/add-product-fixed-bundle
 * @param {Object} reward - Reward tier object with products
 * @param {string|number} existingBundleId - Existing bundle product ID (for updates, can be numeric or GID)
 * @param {Object} options - Options object to control what gets updated
 * @param {boolean} options.updateProducts - Whether to update bundle components (default: true)
 * @param {boolean} options.updateDescription - Whether to update description (default: true)
 * @param {boolean} options.updatePrice - Whether to update price (default: true)
 * @param {Object} oldReward - Previous reward state for comparison (optional)
 * @returns {Promise<Object>} Object with bundle_product_id and bundle_product_handle
 */
export async function createOrUpdateRewardBundle(reward, existingBundleId = null, options = {}, oldReward = null) {
    if (!reward.products || reward.products.length === 0) {
        throw new Error('Reward must have products to create a bundle');
    }

    // Default options - update everything unless specified
    const {
        updateProducts = true,
        updateTitle = true,
        updateDescription = true,
        updatePrice = true
    } = options;

    const title = reward.reward_title || 'Reward Bundle';

    // Normalize existingBundleId to GID format and validate it's a bundle
    let normalizedExistingBundleId = null;
    let shouldUpdate = false;
    let bundleProductId = null;

    if (existingBundleId) {
        // Normalize bundle ID to GID format
        normalizedExistingBundleId = normalizeProductGID(existingBundleId);
        bundleProductId = normalizedExistingBundleId;
        
        if (!normalizedExistingBundleId) {
            console.warn(`Invalid existing bundle ID format: ${existingBundleId}, will create new bundle`);
        } else {
            // Validate that the existing ID is actually a bundle using bundleComponents query
            const isValidBundle = await isBundleProduct(normalizedExistingBundleId);
            
            if (isValidBundle) {
                // Bundle exists and is confirmed to be a bundle
                shouldUpdate = true;
            } else {
                // Product might exist but is not a bundle, or doesn't exist
                console.warn(`Product ${normalizedExistingBundleId} is not a bundle, will create new bundle`);
                normalizedExistingBundleId = null;
                bundleProductId = null;
            }
        }
    }

    // If bundle exists and we only need to update specific fields, do that
    if (shouldUpdate && bundleProductId && !updateProducts) {
        // Bundle exists, only update specific fields
        const updatePromises = [];
        
        if (updateTitle && reward.reward_title) {
            updatePromises.push(
                updateBundleTitleOnly(bundleProductId, reward.reward_title)
                    .catch(err => console.warn('Error updating title:', err))
            );
        }
        
        if (updateDescription && reward.reward_description) {
            updatePromises.push(
                updateBundleProductDescription(bundleProductId, reward.reward_description)
                    .catch(err => console.warn('Error updating description:', err))
            );
        }
        
        if (updatePrice && reward.reward_price) {
            updatePromises.push(
                updateBundlePriceOnly(bundleProductId, reward.reward_price, reward.products)
                    .catch(err => console.warn('Error updating price:', err))
            );
        }
        
        // Wait for updates to complete
        if (updatePromises.length > 0) {
            await Promise.allSettled(updatePromises);
        }
        
        // Get bundle handle and return
        const bundleHandle = await getBundleProductHandle(bundleProductId);
        return {
            bundle_product_id: bundleProductId,
            bundle_product_handle: bundleHandle
        };
    }

    // OPTIMIZATION: Batch check all products for bundle status in a single GraphQL query
    const bundleProductIds = [];
    const validProductIds = [];
    
    // Collect all product IDs to check
    const productIdsToCheck = reward.products
        .filter(p => p.shopify_product_id)
        .map(p => p.shopify_product_id);
    
    if (productIdsToCheck.length > 0) {
        try {
            // Batch check all products at once
            const bundleCheckResults = await batchIsBundleProduct(productIdsToCheck);
            
            // Process results
            for (const product of reward.products) {
                if (!product.shopify_product_id) continue;
                
                // Normalize product ID to GID for lookup
                let productGid = product.shopify_product_id;
                if (typeof product.shopify_product_id === 'string' && product.shopify_product_id.includes('gid://')) {
                    productGid = product.shopify_product_id;
                } else {
                    productGid = normalizeProductGID(product.shopify_product_id);
                }
                
                const isBundle = bundleCheckResults.get(productGid) || false;
                
                if (isBundle) {
                    bundleProductIds.push({
                        id: product.shopify_product_id,
                        title: product.product_title || 'Unknown Product'
                    });
                } else {
                    validProductIds.push({
                        id: product.shopify_product_id,
                        title: product.product_title || 'Unknown Product'
                    });
                }
            }
        } catch (error) {
            console.warn('Error in batch bundle check:', error);
            // Fallback: assume all are valid (not bundles)
            reward.products.forEach(product => {
                if (product.shopify_product_id) {
                    validProductIds.push({
                        id: product.shopify_product_id,
                        title: product.product_title || 'Unknown Product'
                    });
                }
            });
        }
    }

    // If all products are bundles, throw a more descriptive error
    if (bundleProductIds.length > 0 && validProductIds.length === 0) {
        const bundleProductTitles = bundleProductIds.map(p => p.title).join(', ');
        throw new Error(`Cannot create bundle: All selected products are already bundles. Bundle products cannot be used as components. Please select regular products instead. (Bundle products: ${bundleProductTitles})`);
    }

    // If some products are bundles, warn but continue with valid products
    if (bundleProductIds.length > 0) {
        const bundleProductTitles = bundleProductIds.map(p => p.title).join(', ');
        console.warn(`Some products are bundles and will be skipped: ${bundleProductTitles}`);
    }

    // Only create/update bundle components if updateProducts is true
    if (!updateProducts && shouldUpdate && bundleProductId) {
        // Bundle exists and we don't need to update products, just return existing bundle
        const bundleHandle = await getBundleProductHandle(bundleProductId);
        return {
            bundle_product_id: bundleProductId,
            bundle_product_handle: bundleHandle
        };
    }

    // Create bundle components from selected variants only (use reward.products, not bundle ID)
    const components = await createBundleComponents(reward.products);

    if (components.length === 0) {
        // Provide more context about why no components were found
        if (bundleProductIds.length > 0) {
            const bundleProductTitles = bundleProductIds.map(p => p.title).join(', ');
            throw new Error(`No valid components found for bundle. All selected products are bundles and cannot be used as components. Please add regular products to this reward. (Bundle products: ${bundleProductTitles})`);
        }
        throw new Error('No valid components found for bundle. Please ensure products have valid variants selected.');
    }

    let operation;

    if (shouldUpdate && normalizedExistingBundleId) {
        // Update existing bundle
        try {
            operation = await updateProductBundle(normalizedExistingBundleId, title, components);
        } catch (error) {
            console.warn('Failed to update bundle, will try to create new bundle:', error.message || error);
            // If update fails, try creating a new bundle
            normalizedExistingBundleId = null;
            operation = await createProductBundle(title, components);
        }
    } else {
        // Create new bundle
        operation = await createProductBundle(title, components);
    }

    if (!operation || !operation.id) {
        throw new Error('Failed to create bundle operation');
    }

    // Poll for completion
    const result = await pollBundleOperation(operation.id);

    if (result.status === 'COMPLETE' && result.productId) {
        bundleProductId = result.productId;
        
        // Set bundle product status to UNLISTED immediately after creation
        // UNLISTED products are hidden from search, collections, and recommendations
        // but can be accessed via direct link, handle, or metafield reference
        // Reference: https://shopify.dev/docs/api/admin-graphql/latest/enums/productstatus
        const statusUpdated = await setProductStatusUnlisted(bundleProductId);
        if (!statusUpdated) {
            console.warn(`Bundle created but could not set status to UNLISTED for product ${bundleProductId}`);
            // Don't fail - bundle was created successfully, just status update failed
        }

        // Update bundle product description with reward description if provided and updateDescription is true
        if (updateDescription && reward.reward_description) {
            try {
                const descriptionUpdated = await updateBundleProductDescription(bundleProductId, reward.reward_description);
                if (!descriptionUpdated) {
                    console.warn(`Bundle created but could not update description for product ${bundleProductId}`);
                    // Don't fail - bundle was created successfully, just description update failed
                }
            } catch (error) {
                console.warn(`Error updating bundle product description for ${bundleProductId}:`, error.message || error);
                // Don't fail - bundle was created successfully, just description update failed
            }
        }

        // Update variant prices if reward_price is provided and updatePrice is true
        if (updatePrice && reward.reward_price) {
            try {
                // Calculate total price of all selected products
                const totalProductPrice = calculateTotalProductPrice(reward.products);
                
                const priceUpdated = await updateBundleVariantPrices(
                    bundleProductId, 
                    reward.reward_price, 
                    totalProductPrice
                );
                if (!priceUpdated) {
                    console.warn(`Bundle created but could not update variant prices for product ${bundleProductId}`);
                    // Don't fail - bundle was created successfully, just price update failed
                }
            } catch (error) {
                console.warn(`Error updating variant prices for bundle ${bundleProductId}:`, error.message || error);
                // Don't fail - bundle was created successfully, just price update failed
            }
        }

        // Get bundle product handle
        const bundleHandle = await getBundleProductHandle(bundleProductId);

        return {
            bundle_product_id: bundleProductId,
            bundle_product_handle: bundleHandle
        };
    }

    throw new Error('Bundle creation did not complete successfully');
}

/**
 * Batch get inventoryItem IDs from multiple variant IDs in a single GraphQL query
 * @param {Array<string>} variantIds - Array of variant GIDs
 * @returns {Promise<Map<string, string|null>>} Map of variantId -> inventoryItemId (or null)
 */
async function batchGetInventoryItemIds(variantIds) {
    if (!variantIds || variantIds.length === 0) {
        return new Map();
    }

    const results = new Map();
    const normalizedIds = [];

    // Normalize all variant IDs
    for (const variantId of variantIds) {
        if (!variantId) {
            results.set(String(variantId), null);
            continue;
        }

        let variantGid = variantId;
        if (typeof variantId === 'number') {
            variantGid = `gid://shopify/ProductVariant/${variantId}`;
        } else if (!variantId.includes('gid://')) {
            variantGid = `gid://shopify/ProductVariant/${variantId}`;
        }

        normalizedIds.push(variantGid);
    }

    if (normalizedIds.length === 0) {
        return results;
    }

    try {
        // Build GraphQL query with aliases for each variant
        const queryParts = normalizedIds.map((id, index) => {
            const alias = `variant${index}`;
            return `
                ${alias}: productVariant(id: $id${index}) {
                    id
                    inventoryItem {
                        id
                    }
                }
            `;
        });

        const variables = {};
        normalizedIds.forEach((id, index) => {
            variables[`id${index}`] = id;
        });

        const query = {
            query: `
                query BatchGetInventoryItems(${normalizedIds.map((_, i) => `$id${i}: ID!`).join(', ')}) {
                    ${queryParts.join('\n')}
                }
            `,
            variables
        };

        const response = await graphqlCall(query);

        if (response.errors) {
            console.warn('Error in batch inventory item query:', response.errors[0]?.message);
            // Fallback: mark all as null
            normalizedIds.forEach(id => {
                results.set(id, null);
            });
            return results;
        }

        // Process results
        normalizedIds.forEach((id, index) => {
            const alias = `variant${index}`;
            const variant = response.data?.[alias];
            const inventoryItemId = variant?.inventoryItem?.id || null;
            results.set(id, inventoryItemId);
        });

        return results;
    } catch (error) {
        console.warn('Error in batch inventory item query:', error.message || error);
        // Fallback: mark all as null
        normalizedIds.forEach(id => {
            results.set(id, null);
        });
        return results;
    }
}

/**
 * Get inventoryItem ID from variant ID
 * @param {string} variantId - Variant GID (e.g., "gid://shopify/ProductVariant/123")
 * @returns {Promise<string|null>} InventoryItem GID or null if not found
 */
export async function getInventoryItemId(variantId) {
    if (!variantId) return null;

    try {
        // Extract numeric ID from GID format if needed
        let variantGid = variantId;
        if (typeof variantId === 'number') {
            variantGid = `gid://shopify/ProductVariant/${variantId}`;
        } else if (!variantId.includes('gid://')) {
            variantGid = `gid://shopify/ProductVariant/${variantId}`;
        }

        const query = {
            query: `
                query GetVariantInventoryItem($id: ID!) {
                    productVariant(id: $id) {
                        id
                        inventoryItem {
                            id
                        }
                    }
                }
            `,
            variables: {
                id: variantGid
            }
        };

        const response = await graphqlCall(query);

        if (response.errors) {
            console.warn('Error getting inventoryItem ID:', response.errors);
            return null;
        }

        const inventoryItemId = response.data?.productVariant?.inventoryItem?.id;
        return inventoryItemId || null;
    } catch (error) {
        console.warn('Could not get inventoryItem ID for variant:', variantId, error);
        return null;
    }
}

/**
 * Batch update multiple inventoryItem tracked status in a single GraphQL call
 * @param {Array<{id: string, tracked: boolean}>} inventoryItemUpdates - Array of {id, tracked} objects
 * @returns {Promise<Map<string, boolean>>} Map of inventoryItemId -> success boolean
 */
async function batchUpdateInventoryItemTracked(inventoryItemUpdates) {
    if (!inventoryItemUpdates || inventoryItemUpdates.length === 0) {
        return new Map();
    }

    const results = new Map();

    // Filter out invalid entries
    const validUpdates = inventoryItemUpdates.filter(item => item && item.id);
    
    if (validUpdates.length === 0) {
        return results;
    }

    try {
        // Build GraphQL mutation with aliases for each inventory item update
        const mutationParts = validUpdates.map((item, index) => {
            const alias = `update${index}`;
            return `
                ${alias}: inventoryItemUpdate(id: $id${index}, input: $input${index}) {
                    inventoryItem {
                        id
                        tracked
                    }
                    userErrors {
                        field
                        message
                    }
                }
            `;
        });

        const variables = {};
        validUpdates.forEach((item, index) => {
            variables[`id${index}`] = item.id;
            variables[`input${index}`] = {
                tracked: item.tracked !== undefined ? item.tracked : false
            };
        });

        const query = {
            query: `
                mutation BatchUpdateInventoryItems(${validUpdates.map((_, i) => `$id${i}: ID!, $input${i}: InventoryItemInput!`).join(', ')}) {
                    ${mutationParts.join('\n')}
                }
            `,
            variables
        };

        const response = await graphqlCall(query);

        if (response.errors) {
            console.warn('Error in batch inventory item update:', response.errors[0]?.message);
            // Mark all as failed
            validUpdates.forEach(item => {
                results.set(item.id, false);
            });
            return results;
        }

        // Process results
        validUpdates.forEach((item, index) => {
            const alias = `update${index}`;
            const updateResult = response.data?.[alias];
            
            if (!updateResult) {
                results.set(item.id, false);
                return;
            }

            if (updateResult.userErrors && updateResult.userErrors.length > 0) {
                const errorMessages = updateResult.userErrors.map(e => e.message).join(', ');
                console.warn(`User errors updating inventoryItem ${item.id}:`, errorMessages);
                results.set(item.id, false);
                return;
            }

            // Success if inventoryItem is returned
            results.set(item.id, !!updateResult.inventoryItem?.id);
        });

        return results;
    } catch (error) {
        console.warn('Error in batch inventory item update:', error.message || error);
        // Mark all as failed
        validUpdates.forEach(item => {
            results.set(item.id, false);
        });
        return results;
    }
}

/**
 * Update inventoryItem tracked status
 * @param {string} inventoryItemId - InventoryItem GID (e.g., "gid://shopify/InventoryItem/123")
 * @param {boolean} tracked - Whether inventory should be tracked (default: false)
 * @returns {Promise<boolean>} True if update was successful
 */
export async function updateInventoryItemTracked(inventoryItemId, tracked = false) {
    if (!inventoryItemId) return false;

    try {
        const query = {
            query: `
                mutation inventoryItemUpdate($id: ID!, $input: InventoryItemInput!) {
                    inventoryItemUpdate(id: $id, input: $input) {
                        inventoryItem {
                            id
                            tracked
                        }
                        userErrors {
                            field
                            message
                        }
                    }
                }
            `,
            variables: {
                id: inventoryItemId,
                input: {
                    tracked: tracked
                }
            }
        };

        const response = await graphqlCall(query);

        if (response.errors) {
            console.warn('Error updating inventoryItem tracked status:', response.errors);
            return false;
        }

        if (response.data?.inventoryItemUpdate?.userErrors?.length > 0) {
            const errorMessages = response.data.inventoryItemUpdate.userErrors.map(e => e.message).join(', ');
            console.warn('User errors updating inventoryItem:', errorMessages);
            return false;
        }

        return true;
    } catch (error) {
        console.warn('Could not update inventoryItem tracked status:', inventoryItemId, error);
        return false;
    }
}

/**
 * Update inventory tracking for all variants in a reward tier
 * Sets tracked to false for all variants in the reward tier products
 * @param {Object} reward - Reward tier object with products array
 * @returns {Promise<Object>} Result object with success count and errors
 */
export async function updateRewardVariantsInventoryTracking(reward) {
    if (!reward || !reward.products || reward.products.length === 0) {
        return { success: 0, failed: 0, errors: [] };
    }

    const results = {
        success: 0,
        failed: 0,
        errors: []
    };

    // Collect all variant IDs from reward products
    const variantIds = [];
    reward.products.forEach(product => {
        // First, try selectedVariants (array of variant IDs in GID format)
        if (product.selectedVariants && Array.isArray(product.selectedVariants)) {
            product.selectedVariants.forEach(variantId => {
                if (variantId && !variantIds.includes(variantId)) {
                    variantIds.push(variantId);
                }
            });
        }
        
        // Also check variants array (array of variant objects)
        if (product.variants && Array.isArray(product.variants)) {
            product.variants.forEach(variant => {
                let variantId = null;
                if (typeof variant === 'string') {
                    variantId = variant;
                } else if (typeof variant === 'object' && variant.id) {
                    variantId = variant.id;
                }
                if (variantId && !variantIds.includes(variantId)) {
                    variantIds.push(variantId);
                }
            });
        }
        
        // Fallback: check shopify_variant_id if available
        if (product.shopify_variant_id && !variantIds.includes(product.shopify_variant_id)) {
            variantIds.push(product.shopify_variant_id);
        }
    });

    if (variantIds.length === 0) {
        return results;
    }

    // OPTIMIZATION: Batch get all inventoryItem IDs in a single GraphQL query
    const inventoryItemMap = await batchGetInventoryItemIds(variantIds);
    
    // OPTIMIZATION: Collect all inventory items to update in a single batch
    const inventoryItemUpdates = [];
    const variantToInventoryItemMap = new Map(); // Map variantId -> inventoryItemId for error reporting
    
    variantIds.forEach(variantId => {
        const inventoryItemId = inventoryItemMap.get(variantId);
        if (inventoryItemId) {
            inventoryItemUpdates.push({ id: inventoryItemId, tracked: false });
            variantToInventoryItemMap.set(variantId, inventoryItemId);
        } else {
            console.warn(`Could not get inventoryItem ID for variant: ${variantId}`);
            results.failed++;
            results.errors.push(`Variant ${variantId}: Could not get inventoryItem ID`);
        }
    });

    // OPTIMIZATION: Batch update all inventory items in a single GraphQL call
    if (inventoryItemUpdates.length > 0) {
        try {
            const batchResults = await batchUpdateInventoryItemTracked(inventoryItemUpdates);
            
            // Process batch results
            variantIds.forEach(variantId => {
                const inventoryItemId = variantToInventoryItemMap.get(variantId);
                if (inventoryItemId) {
                    const success = batchResults.get(inventoryItemId);
                    if (success) {
                        results.success++;
                    } else {
                        results.failed++;
                        results.errors.push(`Variant ${variantId}: Failed to update inventoryItem ${inventoryItemId}`);
                    }
                }
            });
        } catch (error) {
            console.error('Error in batch inventory item update:', error);
            // Mark all as failed
            variantIds.forEach(variantId => {
                const inventoryItemId = variantToInventoryItemMap.get(variantId);
                if (inventoryItemId) {
                    results.failed++;
                    results.errors.push(`Variant ${variantId}: ${error.message || 'Unknown error'}`);
                }
            });
        }
    }

    return results;
}

/**
 * Optimized: Get product variants with inventory items in a single GraphQL query
 * @param {string} productId - Product GID
 * @returns {Promise<Map<string, string>>} Map of variantId -> inventoryItemId
 */
async function getProductVariantsWithInventoryItems(productId) {
    const result = new Map();
    
    if (!productId) {
        return result;
    }

    try {
        // Single query to get variants and their inventory items
        const query = {
            query: `
                query GetProductVariantsWithInventory($id: ID!) {
                    product(id: $id) {
                        id
                        variants(first: 250) {
                            edges {
                                node {
                                    id
                                    inventoryItem {
                                        id
                                    }
                                }
                            }
                        }
                    }
                }
            `,
            variables: {
                id: productId
            }
        };

        const response = await graphqlCall(query);

        if (response.errors) {
            console.warn('Error getting product variants with inventory:', response.errors);
            return result;
        }

        const variants = response.data?.product?.variants?.edges || [];
        
        variants.forEach(edge => {
            const variant = edge.node;
            if (variant?.id && variant?.inventoryItem?.id) {
                result.set(variant.id, variant.inventoryItem.id);
            }
        });

        return result;
    } catch (error) {
        console.warn('Error getting product variants with inventory:', error);
        return result;
    }
}

/**
 * Update inventory tracking for all variants of the main product
 * OPTIMIZED: Single GraphQL query to get variants + inventory items, then batch update
 * Sets tracked to false for all variants of the product
 * @param {string} productId - Product GID (e.g., "gid://shopify/Product/123")
 * @returns {Promise<Object>} Result object with success count and errors
 */
export async function updateMainProductVariantsInventoryTracking(productId) {
    if (!productId) {
        return { success: 0, failed: 0, errors: [] };
    }

    const results = {
        success: 0,
        failed: 0,
        errors: []
    };

    try {
        // OPTIMIZATION: Single query to get variants with inventory items
        const variantToInventoryItemMap = await getProductVariantsWithInventoryItems(productId);

        if (variantToInventoryItemMap.size === 0) {
            return results;
        }

        // Collect all inventory items to update
        const inventoryItemUpdates = [];
        const inventoryItemToVariantMap = new Map(); // Map inventoryItemId -> variantId for error reporting
        
        variantToInventoryItemMap.forEach((inventoryItemId, variantId) => {
            inventoryItemUpdates.push({ id: inventoryItemId, tracked: false });
            inventoryItemToVariantMap.set(inventoryItemId, variantId);
        });

        // OPTIMIZATION: Batch update all inventory items in a single GraphQL call
        if (inventoryItemUpdates.length > 0) {
            try {
                const batchResults = await batchUpdateInventoryItemTracked(inventoryItemUpdates);
                
                // Process batch results
                batchResults.forEach((success, inventoryItemId) => {
                    const variantId = inventoryItemToVariantMap.get(inventoryItemId);
                    if (success) {
                        results.success++;
                    } else {
                        results.failed++;
                        results.errors.push(`Variant ${variantId}: Failed to update inventoryItem ${inventoryItemId}`);
                    }
                });
            } catch (error) {
                console.error('Error in batch inventory item update:', error);
                // Mark all as failed
                variantToInventoryItemMap.forEach((inventoryItemId, variantId) => {
                    results.failed++;
                    results.errors.push(`Variant ${variantId}: ${error.message || 'Unknown error'}`);
                });
            }
        }

        return results;
    } catch (error) {
        console.error('Error updating main product variants inventory tracking:', error);
        results.errors.push(`Product ${productId}: ${error.message || 'Unknown error'}`);
        return results;
    }
}

/**
 * Create a MediaImage from an uploaded file URL
 * @param {string} fileUrl - URL of the uploaded file (from staged upload resourceUrl)
 * @param {string} altText - Alt text for the image (optional)
 * @returns {Promise<string|null>} MediaImage GID or null if creation fails
 */
export async function createMediaImageFromUrl(fileUrl, altText = '') {
    if (!fileUrl) return null;

    try {
        const query = {
            query: `
                mutation fileCreate($files: [FileCreateInput!]!) {
                    fileCreate(files: $files) {
                        files {
                            ... on MediaImage {
                                id
                                image {
                                    url
                                    altText
                                }
                            }
                        }
                        userErrors {
                            field
                            message
                        }
                    }
                }
            `,
            variables: {
                files: [
                    {
                        originalSource: fileUrl,
                        alt: altText || undefined
                    }
                ]
            }
        };

        const response = await graphqlCall(query);

        if (response.errors) {
            console.warn('Error creating MediaImage:', response.errors);
            return null;
        }

        if (response.data?.fileCreate?.userErrors?.length > 0) {
            const errorMessages = response.data.fileCreate.userErrors.map(e => e.message).join(', ');
            console.warn('User errors creating MediaImage:', errorMessages);
            return null;
        }

        const mediaImage = response.data?.fileCreate?.files?.[0];
        if (mediaImage?.id) {
            return mediaImage.id; // Returns GID like "gid://shopify/MediaImage/123"
        }

        return null;
    } catch (error) {
        console.warn('Could not create MediaImage from URL:', fileUrl, error);
        return null;
    }
}

/**
 * Update bundle product image using productSet mutation
 * @param {string} productId - Bundle product GID (e.g., "gid://shopify/Product/123")
 * @param {string} mediaImageId - MediaImage GID (e.g., "gid://shopify/MediaImage/123")
 * @returns {Promise<boolean>} True if update was successful
 */
export async function updateBundleProductImage(productId, mediaImageId) {
    if (!productId || !mediaImageId) return false;

    try {
        // Ensure productId is in GID format
        let productGid = productId;
        if (typeof productId === 'number') {
            productGid = `gid://shopify/Product/${productId}`;
        } else if (!productId.includes('gid://')) {
            productGid = `gid://shopify/Product/${productId}`;
        }

        // Ensure mediaImageId is in GID format
        let mediaGid = mediaImageId;
        if (typeof mediaImageId === 'number') {
            mediaGid = `gid://shopify/MediaImage/${mediaImageId}`;
        } else if (!mediaImageId.includes('gid://')) {
            // Try to determine if it's already a MediaImage ID or needs conversion
            if (mediaImageId.includes('MediaImage')) {
                mediaGid = mediaImageId;
            } else {
                mediaGid = `gid://shopify/MediaImage/${mediaImageId}`;
            }
        }

        const query = {
            query: `
                mutation updateProductImage($input: ProductSetInput!) {
                    productSet(input: $input) {
                        product {
                            id
                        }
                        userErrors {
                            field
                            message
                        }
                    }
                }
            `,
            variables: {
                input: {
                    id: productGid,
                    files: [
                        { id: mediaGid }
                    ]
                }
            }
        };

        const response = await graphqlCall(query);

        if (response.errors) {
            console.warn('Error updating bundle product image:', response.errors);
            return false;
        }

        if (response.data?.productSet?.userErrors?.length > 0) {
            const errorMessages = response.data.productSet.userErrors.map(e => e.message).join(', ');
            console.warn('User errors updating bundle product image:', errorMessages);
            return false;
        }

        if (response.data?.productSet?.product?.id) {
            return true;
        }

        return false;
    } catch (error) {
        console.warn('Could not update bundle product image:', productId, mediaImageId, error);
        return false;
    }
}

/**
 * Upload reward image and update bundle product with it
 * This function handles the complete flow: upload -> create MediaImage -> update product
 * @param {File} imageFile - Image file to upload
 * @param {string} bundleProductId - Bundle product GID
 * @param {string} altText - Alt text for the image (optional)
 * @returns {Promise<Object>} Result object with success status and image URL
 */
export async function uploadAndSetRewardImage(imageFile, bundleProductId, altText = '') {
    if (!imageFile || !bundleProductId) {
        return { success: false, url: null, error: 'Image file and bundle product ID are required' };
    }

    try {
        // Import uploadImageToShopify dynamically
        const { uploadImageToShopify } = await import('../services/shopifyGraphQL');

        // Step 1: Upload image to Shopify (returns CDN URL and mediaImageId when fileCreate ran)
        const uploadResult = await uploadImageToShopify(imageFile, altText);

        if (!uploadResult.url) {
            return { success: false, url: null, error: 'Failed to upload image' };
        }

        // Step 2: Use MediaImage ID from upload if available; otherwise create from URL (legacy path)
        let mediaImageId = uploadResult.mediaImageId || null;
        if (!mediaImageId) {
            mediaImageId = await createMediaImageFromUrl(uploadResult.url, altText);
        }

        if (!mediaImageId) {
            console.warn('Failed to create MediaImage, but image URL is available:', uploadResult.url);
            return { success: true, url: uploadResult.url, mediaImageId: null, productUpdated: false };
        }

        // Step 3: Update bundle product with the image
        const productUpdated = await updateBundleProductImage(bundleProductId, mediaImageId);
        
        if (!productUpdated) {
            console.warn('Failed to update bundle product image, but MediaImage was created');
            // Return success with URL and MediaImage ID even if product update fails
            return { success: true, url: uploadResult.url, mediaImageId: mediaImageId, productUpdated: false };
        }

        return { 
            success: true, 
            url: uploadResult.url, 
            mediaImageId: mediaImageId, 
            productUpdated: true 
        };
    } catch (error) {
        console.error('Error uploading and setting reward image:', error);
        return { 
            success: false, 
            url: null, 
            error: error.message || 'Failed to upload and set reward image' 
        };
    }
}

